import { useEffect, useMemo, useRef, useState } from "react";
import api, { setCsrfToken } from "../api/api";
import {
  ArrowLeft,
  ArrowRight,
  BarChart2,
  Building2,
  CheckCircle2,
  CircleCheckBig,
  ClipboardList,
  Headphones,
  KeyRound,
  LayoutGrid,
  Loader2,
  LockKeyhole,
  Receipt,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  User,
  Users,
} from "lucide-react";

import { TePidIcon } from "../components/TePidLogo";
import { useToast } from "../context/ToastContext";

const OTP_LENGTH     = 6;
const RESEND_SECONDS = 30;

const FEATURES = [
  { Icon: ClipboardList, title: "Task Management",  desc: "Assign, track, and close field tasks in real time." },
  { Icon: ShoppingCart,  title: "Purchase Orders",  desc: "Raise, approve, and monitor POs end-to-end." },
  { Icon: Receipt,       title: "Expense Tracking", desc: "Capture and approve team expenses on the go." },
  { Icon: BarChart2,     title: "Work Reports",     desc: "Daily logs, timesheets, and performance insights." },
];

const STATS = [
  { Icon: Users,      value: "100+", label: "Field Agents" },
  { Icon: Building2,  value: "6",    label: "Departments"  },
  { Icon: LayoutGrid, value: "5",    label: "Modules"      },
  { Icon: Headphones, value: "24/7", label: "Support"      },
];

const stepConfig = [
  { id: 1, label: "Mobile",   icon: Smartphone    },
  { id: 2, label: "OTP",      icon: ShieldCheck   },
  { id: 3, label: "Password", icon: KeyRound      },
  { id: 4, label: "Success",  icon: CircleCheckBig },
];

function getErrorMessage(err, fallback) {
  if (err.userMessage) return err.userMessage;

  // HTTP response received — parse the body first
  if (err.response) {
    const status = err.response.status;
    const d      = err.response?.data?.detail;
    const detail = Array.isArray(d) ? d[0]?.msg : d;

    // Gateway / server errors → tell the user the backend is having issues
    if (status === 502) return "Backend gateway error (502). The server may be starting up — please wait 30 seconds and try again.";
    if (status === 503) return "Service unavailable (503). The backend is not running or is restarting — please try again shortly.";
    if (status === 504) return "Request timed out (504). The backend took too long to respond — it may be starting up. Please wait and retry.";
    if (status === 429) return detail || "Too many attempts. Please wait a moment before trying again.";

    // Auth errors
    if (status === 401) return detail || "Invalid credentials. Please check your email and password.";
    if (status === 403) return detail || "Your account is disabled. Contact your administrator.";

    // Validation / generic
    return detail || `Server error (${status}). Please try again.`;
  }

  // No response at all — network-level failure (ERR_FAILED, ERR_CONNECTION_REFUSED, CSP block, etc.)
  if (err.request) {
    // Axios sets err.code for network errors
    const code = err.code || "";
    if (code === "ECONNABORTED" || code === "ETIMEDOUT")
      return "Request timed out. The backend may be starting up — please wait 30 seconds and try again.";
    return "Cannot reach the server. The backend may be offline or still starting up. Please wait a moment and try again.";
  }

  return err.message || fallback;
}

// Present on any 429 from the rate-limit-aware auth endpoints (login lockout,
// OTP resend cooldown, OTP attempts exceeded, OTP/reset request limits) —
// see backend RateLimitExceeded. Absent/0 means "not a timed lockout".
function getRetryAfterSeconds(err) {
  const v = err?.response?.data?.retry_after_seconds;
  return typeof v === "number" && v > 0 ? Math.ceil(v) : 0;
}

function formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
const normalizeMobile = (v) => (v || "").replace(/[^\d+]/g, "");
const countDigits     = (v) => (v || "").replace(/\D/g, "").length;

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: "Start typing", color: "bg-white/15", text: "text-white/30" };
  let s = 0;
  if (pw.length >= 8)          s++;
  if (/[A-Z]/.test(pw))        s++;
  if (/[a-z]/.test(pw))        s++;
  if (/\d/.test(pw))           s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 2) return { score: s, label: "Weak",   color: "bg-rose-500",    text: "text-rose-400"    };
  if (s <= 4) return { score: s, label: "Good",   color: "bg-amber-400",   text: "text-amber-300"   };
  return              { score: s, label: "Strong", color: "bg-emerald-500", text: "text-emerald-400" };
}

function SpinnerLabel({ label }) {
  return <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{label}</span>;
}

function AnimatedPanel({ children, panelKey }) {
  return <div key={panelKey} className="animate-[fadeSlideIn_.25s_ease]">{children}</div>;
}

function LockoutBanner({ title, message, seconds }) {
  if (!seconds || seconds <= 0) return null;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      background: "rgba(244,63,94,0.10)", border: "1px solid rgba(244,63,94,0.28)",
      borderRadius: 14, padding: "14px 16px",
    }}>
      <LockKeyhole style={{ width: 18, height: 18, color: "#fb7185", flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13.5, fontWeight: 700, color: "#fda4af", margin: 0 }}>{title}</p>
        <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.55)", margin: "3px 0 10px", lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontVariantNumeric: "tabular-nums",
            fontSize: 17, fontWeight: 800, color: "#fff",
            background: "rgba(255,255,255,0.08)", borderRadius: 8,
            padding: "3px 10px", letterSpacing: 0.5,
          }}>
            {formatMMSS(seconds)}
          </span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>until you can try again</span>
        </div>
      </div>
    </div>
  );
}

function StepPill({ currentStep }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
      {stepConfig.map((step, i) => {
        const Icon    = step.icon;
        const active  = currentStep === step.id;
        const done    = currentStep > step.id;
        return (
          <div key={step.id} style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              borderRadius: 99, padding: "5px 12px",
              border: active ? "1px solid rgba(59,130,246,0.5)" : done ? "1px solid rgba(52,211,153,0.4)" : "1px solid rgba(255,255,255,0.10)",
              background: active ? "rgba(59,130,246,0.18)" : done ? "rgba(52,211,153,0.14)" : "rgba(255,255,255,0.04)",
              fontSize: 12, fontWeight: 600,
              color: active ? "#93c5fd" : done ? "#6ee7b7" : "rgba(255,255,255,0.28)",
            }}>
              <Icon style={{ width: 13, height: 13 }} />
              <span>{step.label}</span>
            </div>
            {i < stepConfig.length - 1 && (
              <div style={{ width: 16, height: 1, background: done ? "rgba(52,211,153,0.35)" : "rgba(255,255,255,0.10)", flexShrink: 0 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function OtpBoxes({ otpDigits, setOtpDigits, disabled }) {
  const refs = useRef([]);
  const focus = (i) => { refs.current[i]?.focus(); refs.current[i]?.select?.(); };
  const handleChange = (idx, val) => {
    const d = val.replace(/\D/g, "").slice(-1);
    const n = [...otpDigits]; n[idx] = d; setOtpDigits(n);
    if (d && idx < OTP_LENGTH - 1) focus(idx + 1);
  };
  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (otpDigits[idx]) { const n = [...otpDigits]; n[idx] = ""; setOtpDigits(n); }
      else if (idx > 0) focus(idx - 1);
    }
    if (e.key === "ArrowLeft"  && idx > 0)              focus(idx - 1);
    if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) focus(idx + 1);
  };
  const handlePaste = (e) => {
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!p) return; e.preventDefault();
    setOtpDigits(Array.from({ length: OTP_LENGTH }, (_, i) => p[i] || ""));
    focus(Math.min(p.length, OTP_LENGTH - 1));
  };
  return (
    <div style={{ marginBottom: 4 }}>
      <div className="login-otp-grid" style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 10, marginBottom: 8 }}>
        {otpDigits.map((digit, idx) => (
          <input key={idx} ref={(n) => { refs.current[idx] = n; }}
            type="text" inputMode="numeric" autoComplete={idx === 0 ? "one-time-code" : "off"}
            maxLength={1} disabled={disabled} value={digit}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={handlePaste}
            style={{
              width: "100%", minWidth: 0, boxSizing: "border-box",
              height: 52, borderRadius: 12,
              border: digit ? "1px solid rgba(59,130,246,0.60)" : "1px solid rgba(255,255,255,0.13)",
              background: digit ? "rgba(37,99,235,0.22)" : "rgba(255,255,255,0.07)",
              textAlign: "center", fontSize: 20, fontWeight: 700, color: "#fff",
              outline: "none", transition: "all 0.15s",
            }}
          />
        ))}
      </div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.30)" }}>Enter the 6-digit code sent to your phone.</p>
    </div>
  );
}

export default function Login({ onLogin }) {
  const toast = useToast();

  const [view,            setView]            = useState("login");
  const [identifier,      setIdentifier]      = useState("");
  const [password,        setPassword]        = useState("");
  const [resetMobile,     setResetMobile]     = useState("");
  const [otpDigits,       setOtpDigits]       = useState(Array(OTP_LENGTH).fill(""));
  // Backend-issued proof that the OTP was actually verified (see
  // verifyOtp) — set only from a real verified:true response, held only in
  // memory (never localStorage/sessionStorage), and the only thing sent to
  // /forgot-password/reset. There is no client path to step 3 that doesn't
  // go through a real backend verification first.
  const [resetToken,      setResetToken]      = useState(null);
  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotStep,      setForgotStep]      = useState(1);
  const [countdown,       setCountdown]       = useState(0);
  const [loading,         setLoading]         = useState("");
  const [showPw,          setShowPw]          = useState(false);
  const [showNewPw,       setShowNewPw]       = useState(false);
  // Real, server-driven lockouts — distinct from the friendly post-send
  // "Resend in Ns" hint above, which is purely a client-side courtesy timer.
  const [loginLockout,    setLoginLockout]    = useState(null); // { seconds }
  const [otpLockReason,   setOtpLockReason]   = useState(null); // string | null

  const otpValue = otpDigits.join("");
  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, []);
  useEffect(() => {
    if (countdown <= 0) {
      setOtpLockReason(null);
      return;
    }
    const t = setTimeout(() => setCountdown((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);
  useEffect(() => {
    if (!loginLockout) return;
    if (loginLockout.seconds <= 0) { setLoginLockout(null); return; }
    const t = setTimeout(
      () => setLoginLockout((p) => (p ? { ...p, seconds: p.seconds - 1 } : p)),
      1000
    );
    return () => clearTimeout(t);
  }, [loginLockout]);

  const saveSession = (data) => {
    const { user, access_token } = data;
    const role = user.role || user.roles?.[0] || "employee";
    localStorage.setItem("role", role);
    localStorage.setItem("user", JSON.stringify(user));
    if (access_token) {
      localStorage.setItem("token", access_token);
    }
    onLogin(user, role);
  };

  const clearForgotFlow = () => {
    setResetMobile(""); setOtpDigits(Array(OTP_LENGTH).fill(""));
    setNewPassword(""); setConfirmPassword(""); setForgotStep(1); setCountdown(0);
    setOtpLockReason(null); setResetToken(null);
  };
  const openForgotPassword = () => { setView("forgot"); clearForgotFlow(); };
  const openLogin          = () => { setView("login"); setLoading(""); clearForgotFlow(); setLoginLockout(null); };

  const validateMobile = () => {
    const n = normalizeMobile(resetMobile);
    if (!n) return "Mobile number is required.";
    if (countDigits(n) < 10) return "Enter at least 10 digits.";
    return null;
  };
  const validateResetPassword = () => {
    if (!newPassword || newPassword.length < 8) return "Password must be at least 8 characters.";
    if (newPassword !== confirmPassword) return "Passwords do not match.";
    return null;
  };

  const handleLogin = async () => {
    if (!identifier.trim()) { toast.error("Enter your email or mobile number."); return; }
    if (!password)          { toast.error("Enter your password."); return; }
    setLoading("login");
    try {
      const res = await api.post("/auth/login/web", {
        identifier: identifier.trim(), password,
      });
      setCsrfToken(res.data.csrf_token);
      setLoginLockout(null);
      saveSession(res.data);
      toast.success("Signed in successfully.");
    } catch (err) {
      const retryAfter = getRetryAfterSeconds(err);
      if (retryAfter > 0) {
        setLoginLockout({ seconds: retryAfter });
      } else {
        toast.error(getErrorMessage(err, "Login failed"));
      }
    } finally { setLoading(""); }
  };

  const requestOtp = async ({ resend = false } = {}) => {
    const e = validateMobile();
    if (e) { toast.error(e); return; }
    setLoading(resend ? "resend" : "request-otp");
    try {
      const res = await api.post("/auth/forgot-password/request-otp", {
        mobile: normalizeMobile(resetMobile),
      });
      // The backend only guarantees the SMS provider accepted the request,
      // not that it reached the handset — but `sent: false` (e.g. dry-run)
      // means no request was even made, so never advance to OTP entry then.
      if (res.data?.sent === true) {
        setOtpDigits(Array(OTP_LENGTH).fill(""));
        setOtpLockReason(null);
        setForgotStep(2); setCountdown(RESEND_SECONDS);
        toast.success(resend ? "OTP resent." : "OTP sent to your mobile.");
      } else {
        toast.info(res.data?.message || "OTP was not sent.");
      }
    } catch (err) {
      const retryAfter = getRetryAfterSeconds(err);
      if (retryAfter > 0) {
        setOtpLockReason("Too many attempts.");
        setCountdown(retryAfter);
      } else {
        toast.error(getErrorMessage(err, "OTP request failed"));
      }
    } finally { setLoading(""); }
  };

  const verifyOtp = async () => {
    if (otpValue.length !== OTP_LENGTH) { toast.error("Enter the full 6-digit OTP."); return; }
    setLoading("verify-otp");
    try {
      const res = await api.post("/auth/forgot-password/verify-otp", {
        mobile: normalizeMobile(resetMobile), otp: otpValue,
      });
      // The backend is the sole judge of OTP correctness — only advance to
      // the new-password screen on an explicit verified:true plus the
      // reset_token that authorizes the next call. OTP length/format was
      // already checked above, but that's UI validation only, never proof.
      if (res.data?.verified === true && res.data?.reset_token) {
        setResetToken(res.data.reset_token);
        setForgotStep(3);
      } else {
        toast.error("Invalid OTP. Please check the code and try again.");
      }
    } catch (err) {
      const msg = getErrorMessage(err, "OTP verification failed");
      const retryAfter = getRetryAfterSeconds(err);
      if (retryAfter > 0) {
        setOtpLockReason(/otp/i.test(msg) ? "Too many OTP attempts." : "Too many attempts.");
        setCountdown(retryAfter);
      } else {
        toast.error(msg);
      }
    } finally { setLoading(""); }
  };

  const handleForgotReset = async () => {
    const e = validateResetPassword();
    if (e) { toast.error(e); return; }
    if (!resetToken) {
      // Unreachable via normal navigation (step 3 is only shown after
      // verifyOtp populates this) but never attempt a reset without proof
      // of OTP verification.
      toast.error("Your session expired. Please verify the OTP again.");
      setForgotStep(2);
      return;
    }
    setLoading("reset");
    try {
      await api.post("/auth/forgot-password/reset-v2", {
        reset_token: resetToken, new_password: newPassword,
      });
      setResetToken(null);
      setForgotStep(4);
      toast.success("Password reset successfully.");
    } catch (err) {
      const msg = getErrorMessage(err, "Password reset failed");
      const retryAfter = getRetryAfterSeconds(err);
      if (retryAfter > 0) {
        setOtpLockReason("Too many attempts.");
        setCountdown(retryAfter);
        setForgotStep(2);
      } else if (err?.response?.status === 401) {
        // Reset token expired/consumed — the OTP proof is dead, so retrying
        // this same form can't succeed; a fresh OTP is required.
        setResetToken(null);
        toast.error("Your session expired. Please verify the OTP again.");
        setForgotStep(2);
      } else {
        toast.error(msg);
      }
    } finally { setLoading(""); }
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (view === "login") return handleLogin();
    if (forgotStep === 1) return requestOtp();
    if (forgotStep === 2) return verifyOtp();
    if (forgotStep === 3) return handleForgotReset();
  };

  const otpLocked = !!otpLockReason && countdown > 0;
  const submitDisabled =
    loading ||
    (view === "login" && (!identifier.trim() || !password || (loginLockout?.seconds > 0))) ||
    (view === "forgot" &&
      ((forgotStep === 1 && (!resetMobile.trim() || otpLocked)) ||
       (forgotStep === 2 && otpValue.length !== OTP_LENGTH) ||
       (forgotStep === 3 && (!newPassword || !confirmPassword))));

  /* ─── shared input style ─── */
  const inputStyle = {
    width: "100%", height: 56,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.07)",
    color: "#fff", fontSize: 14,
    paddingLeft: 44, paddingRight: 16,
    outline: "none",
    transition: "border 0.2s, background 0.2s",
    boxSizing: "border-box",
  };

  /* ─── shared primary button ─── */
  const primaryBtn = {
    width: "100%", height: 58,
    borderRadius: 14,
    background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)",
    color: "#fff", fontSize: 16, fontWeight: 700,
    border: "none", cursor: "pointer",
    boxShadow: "0 8px 28px rgba(37,99,235,0.45)",
    transition: "transform 0.15s, box-shadow 0.15s",
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <div className="login-outer" style={{ display: "flex", minHeight: "100vh", width: "100%", overflow: "hidden", background: "#060d1f" }}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px rgba(15,25,60,0.95) inset !important;
          -webkit-text-fill-color: #fff !important;
          caret-color: #fff;
        }
        .login-primary-btn:hover { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(37,99,235,0.55) !important; }
        .login-primary-btn:active { transform: translateY(0); }
        .feature-card:hover { background: rgba(15,42,88,0.82) !important; }

        /* ── Mobile ── */
        @media (max-width: 767px) {
          .login-left-panel  { display: none !important; }
          .login-divider     { display: none !important; }
          .login-right-panel {
            padding: 24px 16px !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 100vh !important;
          }
          .login-card {
            transform: none !important;
            padding: 28px 20px !important;
            border-radius: 20px !important;
            min-height: unset !important;
            margin: auto !important;
          }
          .login-mobile-header {
            display: flex !important;
          }
          .login-otp-grid {
            gap: 6px !important;
          }
          .login-otp-grid input {
            height: 44px !important;
            font-size: 18px !important;
          }
          .login-step-pill {
            flex-wrap: wrap;
            gap: 6px !important;
          }
        }
        /* ── Tablet ── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .login-left-panel { width: 45% !important; padding: 40px 32px 32px 40px !important; }
          .login-card       { padding: 36px 32px !important; transform: none !important; }
          .login-right-panel { padding: 32px 24px !important; }
        }
        .login-mobile-header { display: none; }
      `}</style>

      {/* ═══════════════ LEFT PANEL ═══════════════ */}
      <div className="login-left-panel" style={{
        position: "relative", width: "55%", flexShrink: 0,
        display: "flex", flexDirection: "column",
        padding: "56px 56px 40px 64px",
        overflow: "hidden",
        background: "linear-gradient(160deg, #060e26 0%, #0b1840 45%, #060d24 100%)",
      }}>
        {/* Blue glow — primary center */}
        <div style={{
          position: "absolute", top: "8%", left: "-10%",
          width: 700, height: 700, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.20) 0%, rgba(37,99,235,0.06) 45%, transparent 68%)",
          pointerEvents: "none",
        }} />
        {/* Blue glow — top right accent */}
        <div style={{
          position: "absolute", top: "-18%", right: "2%",
          width: 440, height: 440, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(14,165,233,0.13) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        {/* Blue glow — bottom accent */}
        <div style={{
          position: "absolute", bottom: "10%", right: "15%",
          width: 320, height: 320, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.10) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        {/* Network node SVG — subtle field-operations data viz feel */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity: 0.10 }}
          viewBox="0 0 800 700" preserveAspectRatio="xMidYMid slice">
          {[[110,170],[275,85],[445,155],[605,75],[725,195],[685,345],[525,415],[355,375],[185,315],[75,445],[245,515],[425,555],[585,485],[715,545],[370,240],[540,295]].map(([x,y],i) => (
            <circle key={`n${i}`} cx={x} cy={y} r="4.5" fill="rgba(96,165,250,1)" />
          ))}
          {[[110,170,275,85],[275,85,445,155],[445,155,605,75],[605,75,725,195],[725,195,685,345],[685,345,525,415],[525,415,355,375],[355,375,185,315],[185,315,110,170],[185,315,75,445],[355,375,245,515],[525,415,425,555],[685,345,585,485],[585,485,715,545],[425,555,585,485],[275,85,370,240],[445,155,540,295],[370,240,540,295],[370,240,355,375],[540,295,525,415]].map(([x1,y1,x2,y2],i) => (
            <line key={`l${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" />
          ))}
        </svg>
        {/* Bottom fade for stats readability */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 160,
          pointerEvents: "none",
          background: "linear-gradient(to top, rgba(6,13,31,0.92) 0%, transparent 100%)",
        }} />

        {/* ── Logo ── */}
        <div style={{ position: "relative", zIndex: 10, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, background: "#2563eb",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            boxShadow: "0 4px 16px rgba(37,99,235,0.45)",
          }}>
            <TePidIcon size={28} color="white" />
          </div>
          <div style={{ lineHeight: 1.35 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: 0.3 }}>TePid</div>
            <div style={{ fontSize: 10, fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: 2, textTransform: "uppercase" }}>
              Industries PVT LTD
            </div>
          </div>
        </div>

        {/* ── Content fills remaining height, stats pinned to bottom ── */}
        <div style={{
          position: "relative", zIndex: 10, flex: 1,
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          paddingTop: 36,
        }}>

          {/* Top content block */}
          <div style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 720 }}>

            {/* Badge */}
            <div style={{ display: "inline-flex", alignSelf: "flex-start" }}>
              <span style={{
                borderRadius: 99, fontSize: 10, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.20em",
                color: "rgba(255,255,255,0.58)",
                border: "1px solid rgba(255,255,255,0.20)",
                background: "rgba(255,255,255,0.06)",
                padding: "7px 18px",
              }}>
                Enterprise Field Operations Platform
              </span>
            </div>

            {/* Headline */}
            <div>
              <h1 style={{
                fontSize: 52,
                fontWeight: 900, color: "#ffffff",
                lineHeight: 1.12, margin: 0,
                marginBottom: 0, maxWidth: 640,
              }}>
                Manage every field<br />
                operation from one<br />
                intelligent platform
              </h1>
              <p style={{
                fontSize: 16, lineHeight: 1.65,
                color: "rgba(226,232,240,0.82)",
                maxWidth: 560, margin: 0, marginTop: 22,
              }}>
                Streamline task execution, purchase orders, expense tracking, and work reporting
                with a secure, centralized system built for operational visibility, team
                coordination, and faster decision-making.
              </p>
            </div>

            {/* Feature cards 2×2 */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 18, maxWidth: 720 }}>
              {FEATURES.map(({ Icon, title, desc }) => (
                <div
                  key={title}
                  className="feature-card"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 14,
                    borderRadius: 16,
                    background: "rgba(15,32,64,0.58)",
                    border: "1px solid rgba(148,163,184,0.22)",
                    backdropFilter: "blur(14px)",
                    padding: "20px",
                    cursor: "default",
                    transition: "background 0.2s",
                  }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%",
                    background: "#2563eb", flexShrink: 0, marginTop: 2,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(37,99,235,0.45)",
                  }}>
                    <Icon style={{ width: 21, height: 21, color: "#fff" }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 16, fontWeight: 700, color: "rgba(255,255,255,0.95)", lineHeight: 1.3, marginBottom: 5 }}>
                      {title}
                    </p>
                    <p style={{ fontSize: 13, color: "rgba(226,232,240,0.65)", lineHeight: 1.55 }}>
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats + copyright — pinned to bottom */}
          <div>
            <div style={{
              display: "flex", alignItems: "center",
              borderTop: "1px solid rgba(255,255,255,0.18)",
              paddingTop: 22, marginBottom: 14,
            }}>
              {STATS.map(({ Icon, value, label }, i) => (
                <div
                  key={label}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    ...(i > 0 ? {
                      borderLeft: "1px solid rgba(255,255,255,0.11)",
                      paddingLeft: 28, marginLeft: 28,
                    } : {}),
                  }}
                >
                  <Icon style={{ width: 18, height: 18, color: "rgba(255,255,255,0.58)", flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 19, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{value}</p>
                    <p style={{ fontSize: 9, color: "rgba(255,255,255,0.52)", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.13em" }}>
                      {label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.40)" }}>
              © 2025 TePid Industries Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Vertical divider */}
      <div className="login-divider" style={{ width: 1, flexShrink: 0, background: "rgba(255,255,255,0.08)" }} />

      {/* ═══════════════ RIGHT PANEL ═══════════════ */}
      <div className="login-right-panel" style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "48px 40px",
        position: "relative", overflow: "hidden",
        background: "#070f1e",
      }}>
        {/* Top-right blue glow */}
        <div style={{
          position: "absolute", top: -100, right: -100,
          width: 480, height: 480, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.13) 0%, transparent 68%)",
          pointerEvents: "none",
        }} />
        {/* Bottom-left secondary glow */}
        <div style={{
          position: "absolute", bottom: -80, left: -60,
          width: 360, height: 360, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        {/* Dot matrix — bottom-right, visible */}
        <div style={{
          position: "absolute", bottom: 0, right: 0,
          width: 340, height: 340, pointerEvents: "none", userSelect: "none",
          opacity: 0.25, zIndex: 1,
        }}>
          <svg width="340" height="340" viewBox="0 0 340 340" fill="none">
            {Array.from({ length: 13 }, (_, row) =>
              Array.from({ length: 13 }, (_, col) => (
                <circle
                  key={`${row}-${col}`}
                  cx={col * 26 + 13} cy={row * 26 + 13} r="2.8"
                  fill="rgba(59,130,246,0.65)"
                />
              ))
            )}
          </svg>
        </div>

        {/* ── Login card ── */}
        <div className="login-card" style={{
          position: "relative", zIndex: 10,
          width: "100%", maxWidth: 480, minHeight: 560,
          background: "rgba(15,23,42,0.72)",
          border: "1px solid rgba(148,163,184,0.22)",
          borderRadius: 26,
          padding: "52px",
          backdropFilter: "blur(18px)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
          transform: "translateX(-24px)",
        }}>
          {/* Mobile-only brand header */}
          <div className="login-mobile-header" style={{
            alignItems: "center", gap: 10, marginBottom: 28,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11, background: "#2563eb",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              boxShadow: "0 4px 14px rgba(37,99,235,0.45)",
            }}>
              <TePidIcon size={22} color="white" />
            </div>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>TePid</div>
              <div style={{ fontSize: 9, fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: 2, textTransform: "uppercase" }}>
                Industries PVT LTD
              </div>
            </div>
          </div>
          {view === "login" ? (
            <AnimatedPanel panelKey="login">
              {/* Logo centered */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 32 }}>
                <div style={{
                  width: 68, height: 68, borderRadius: 18, background: "#2563eb",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 20,
                  boxShadow: "0 12px 32px rgba(37,99,235,0.50)",
                }}>
                  <TePidIcon size={36} color="white" />
                </div>
                <h2 style={{ fontSize: 30, fontWeight: 700, color: "#fff", letterSpacing: -0.5, margin: 0 }}>
                  Welcome back
                </h2>
                <p style={{ fontSize: 15, color: "rgba(255,255,255,0.50)", marginTop: 8 }}>
                  Sign in to access your account.
                </p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Email field */}
                <div>
                  <label style={{
                    display: "block", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.14em",
                    color: "rgba(255,255,255,0.42)", marginBottom: 8,
                  }}>
                    Email / Mobile Number
                  </label>
                  <div style={{ position: "relative" }}>
                    <User style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      width: 16, height: 16, color: "rgba(255,255,255,0.30)",
                    }} />
                    <input
                      type="text"
                      placeholder="Enter email or mobile number"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Password field */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <label style={{
                      fontSize: 11, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.14em",
                      color: "rgba(255,255,255,0.42)",
                    }}>
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={openForgotPassword}
                      style={{ fontSize: 13, fontWeight: 600, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div style={{ position: "relative" }}>
                    <LockKeyhole style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      width: 16, height: 16, color: "rgba(255,255,255,0.30)",
                    }} />
                    <input
                      type={showPw ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{ ...inputStyle, paddingRight: 60 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((p) => !p)}
                      style={{
                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                        fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.38)",
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                      }}
                    >
                      {showPw ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {loginLockout?.seconds > 0 && (
                  <LockoutBanner
                    title="Too many sign-in attempts"
                    message={'For your security, sign-in has been temporarily locked. Double-check your password, or use "Forgot password?" to reset it.'}
                    seconds={loginLockout.seconds}
                  />
                )}

                {/* Sign In */}
                <button
                  onClick={handleLogin}
                  disabled={!!submitDisabled}
                  className="login-primary-btn"
                  style={{
                    ...primaryBtn,
                    opacity: submitDisabled ? 0.55 : 1,
                    cursor: submitDisabled ? "not-allowed" : "pointer",
                    marginTop: 4,
                  }}
                >
                  {loading === "login" ? <SpinnerLabel label="Signing in..." /> : "Sign In"}
                </button>

                {/* Secure notice */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, paddingTop: 4 }}>
                  <LockKeyhole style={{ width: 12, height: 12, color: "rgba(255,255,255,0.22)" }} />
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>Secure and encrypted access</span>
                </div>
              </div>
            </AnimatedPanel>

          ) : (
            <AnimatedPanel panelKey={`forgot-${forgotStep}`}>
              {/* Back row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                <button
                  type="button"
                  onClick={forgotStep === 1 ? openLogin : () => setForgotStep((p) => Math.max(1, p - 1))}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.42)",
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                  }}
                >
                  <ArrowLeft style={{ width: 15, height: 15 }} />
                  {forgotStep === 1 ? "Back to login" : "Previous step"}
                </button>
                <span style={{
                  fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.30)",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 99, padding: "4px 12px",
                }}>
                  Step {forgotStep} of 4
                </span>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 99,
                  padding: "5px 14px", marginBottom: 12,
                  border: "1px solid rgba(59,130,246,0.30)",
                  background: "rgba(37,99,235,0.15)",
                  fontSize: 11.5, fontWeight: 600, color: "#93c5fd",
                }}>
                  <KeyRound style={{ width: 13, height: 13 }} />
                  Forgot Password
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: "0 0 8px" }}>
                  {forgotStep === 1 && "Recover your password"}
                  {forgotStep === 2 && "Verify OTP"}
                  {forgotStep === 3 && "Create a new password"}
                  {forgotStep === 4 && "All set!"}
                </h2>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.38)", lineHeight: 1.6, margin: 0 }}>
                  {forgotStep === 1 && "Enter your registered mobile number to receive a reset OTP."}
                  {forgotStep === 2 && "Enter the 6-digit OTP sent to your mobile number."}
                  {forgotStep === 3 && "Choose a strong new password for your account."}
                  {forgotStep === 4 && "Your password has been updated. You can sign in now."}
                </p>
              </div>

              <StepPill currentStep={forgotStep} />

              {/* Step 1 */}
              {forgotStep === 1 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: "16px 18px" }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(255,255,255,0.38)", marginBottom: 8 }}>
                      Mobile Number
                    </label>
                    <input type="text" placeholder="e.g. 9876543210"
                      value={resetMobile} onChange={(e) => setResetMobile(e.target.value)} onKeyDown={handleKeyDown}
                      style={{ ...inputStyle, paddingLeft: 16 }} />
                    <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.24)", marginTop: 8 }}>With country code: +919876543210</p>
                  </div>
                  <button onClick={() => requestOtp()} disabled={!!submitDisabled}
                    className="login-primary-btn"
                    style={{ ...primaryBtn, opacity: submitDisabled ? 0.55 : 1, cursor: submitDisabled ? "not-allowed" : "pointer" }}>
                    {loading === "request-otp" ? <SpinnerLabel label="Sending OTP..." /> : "Send Reset OTP"}
                  </button>
                </div>
              )}

              {/* Step 2 */}
              {forgotStep === 2 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.22)", borderRadius: 12, padding: "12px 16px", fontSize: 13, color: "#93c5fd" }}>
                    OTP sent to <strong>{normalizeMobile(resetMobile)}</strong>
                  </div>
                  <OtpBoxes otpDigits={otpDigits} setOtpDigits={setOtpDigits} disabled={loading === "resend"} />
                  {otpLocked ? (
                    <LockoutBanner
                      title={otpLockReason}
                      message='Please wait for the timer, then request a fresh OTP.'
                      seconds={countdown}
                    />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: "12px 16px" }}>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.65)", margin: 0 }}>Didn't receive the OTP?</p>
                        <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.28)", margin: "2px 0 0" }}>{countdown > 0 ? `Resend in ${countdown}s` : "Request a new OTP now."}</p>
                      </div>
                      <button type="button" onClick={() => requestOtp({ resend: true })}
                        disabled={countdown > 0 || loading === "resend"}
                        style={{ fontSize: 13, fontWeight: 600, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", opacity: (countdown > 0 || loading === "resend") ? 0.3 : 1 }}>
                        {loading === "resend" ? <SpinnerLabel label="Resending" /> : "Resend"}
                      </button>
                    </div>
                  )}
                  <button onClick={verifyOtp} disabled={!!submitDisabled}
                    className="login-primary-btn"
                    style={{ ...primaryBtn, gap: 8, opacity: submitDisabled ? 0.55 : 1, cursor: submitDisabled ? "not-allowed" : "pointer" }}>
                    {loading === "verify-otp"
                      ? <SpinnerLabel label="Verifying..." />
                      : <>Continue <ArrowRight style={{ width: 16, height: 16 }} /></>}
                  </button>
                </div>
              )}

              {/* Step 3 */}
              {forgotStep === 3 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(255,255,255,0.38)" }}>New Password</label>
                      <button type="button" onClick={() => setShowNewPw((p) => !p)} style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.35)", background: "none", border: "none", cursor: "pointer" }}>{showNewPw ? "Hide" : "Show"}</button>
                    </div>
                    <input type={showNewPw ? "text" : "password"} placeholder="Minimum 8 characters"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} onKeyDown={handleKeyDown}
                      style={{ ...inputStyle, paddingLeft: 16 }} />
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, color: "rgba(255,255,255,0.35)" }}>Strength</span>
                        <span className={strength.text} style={{ fontWeight: 600 }}>{strength.label}</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 5 }}>
                        {Array.from({ length: 5 }, (_, i) => (
                          <div key={i} style={{ height: 5, borderRadius: 99 }}
                            className={i < strength.score ? strength.color : "bg-white/10"} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: 16 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(255,255,255,0.38)", marginBottom: 8 }}>Confirm Password</label>
                    <input type={showNewPw ? "text" : "password"} placeholder="Re-enter new password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} onKeyDown={handleKeyDown}
                      style={{ ...inputStyle, paddingLeft: 16 }} />
                    {confirmPassword && (
                      <p style={{ marginTop: 8, fontSize: 12, fontWeight: 500 }}
                        className={confirmPassword === newPassword ? "text-emerald-400" : "text-rose-400"}>
                        {confirmPassword === newPassword ? "Passwords match." : "Passwords do not match."}
                      </p>
                    )}
                  </div>
                  <button onClick={handleForgotReset} disabled={!!submitDisabled}
                    className="login-primary-btn"
                    style={{ ...primaryBtn, opacity: submitDisabled ? 0.55 : 1, cursor: submitDisabled ? "not-allowed" : "pointer" }}>
                    {loading === "reset" ? <SpinnerLabel label="Resetting..." /> : "Reset Password"}
                  </button>
                </div>
              )}

              {/* Step 4 */}
              {forgotStep === 4 && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, textAlign: "center" }}>
                  <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.30)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckCircle2 style={{ width: 40, height: 40, color: "#34d399" }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: "0 0 8px" }}>Password updated</h3>
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.38)", margin: 0 }}>Your account is ready. Sign in with your new password.</p>
                  </div>
                  <button type="button" onClick={openLogin}
                    className="login-primary-btn"
                    style={{ ...primaryBtn, gap: 8, width: "100%" }}>
                    Go to Login <ArrowRight style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              )}
            </AnimatedPanel>
          )}
        </div>
      </div>
    </div>
  );
}
